import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Star, Clock, ArrowRight, PhoneCall, Home as HomeIcon, Building2, Droplets, Layers, CloudRain, Flame, Sparkles } from "lucide-react";
import SEO from "../../components/site/SEO";
import { api } from "../../lib/apiClient";
import { T } from "../../lib/testIds";

const iconMap = {
  home: HomeIcon,
  "building-2": Building2,
  droplets: Droplets,
  layers: Layers,
  "cloud-rain": CloudRain,
};

export default function Home() {
  const [services, setServices] = useState([]);
  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  return (
    <>
      <SEO
        title="Wonder Washers Pressure Washing — Oregon's Trusted Experts"
        description="Commercial-grade equipment, residential-grade care. House washing, driveways, decks, roof cleaning, and commercial pressure washing across Oregon. Safe for siding, roofs, and cedar."
        canonical="[BOOKING_URL]/"
      />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1707897283727-31befe824066?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxwcmVzc3VyZSUyMHdhc2hpbmclMjBob3VzZXxlbnwwfHx8fDE3ODU5NTY0NDN8MA&ixlib=rb-4.1.0&q=85"
            alt="Wonder Washers technician pressure washing a driveway"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 ww-hero-overlay" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 min-h-[78vh] flex flex-col justify-center">
          <div className="max-w-3xl text-white ww-fade-in">
            <span className="inline-flex items-center gap-2 rounded-md bg-[color:var(--ww-red)] px-3 py-1.5 text-xs font-subhead tracking-widest uppercase">
              Oregon's Trusted Pressure Washing Experts
            </span>
            <h1 className="mt-6 font-display uppercase text-6xl sm:text-7xl md:text-8xl leading-[0.9]">
              Blast away<br /> the <span className="text-[color:var(--ww-gold)]">grime.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-100/90 leading-relaxed max-w-xl">
              Commercial-grade equipment, residential-grade care. We dial pressure and temperature to the surface — never the other way around.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/book" data-testid={T.home.heroBookBtn} className="ww-btn-primary inline-flex items-center gap-2">
                Book Now <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/quote" data-testid={T.home.heroQuoteBtn} className="inline-flex items-center gap-2 rounded-md bg-white/10 backdrop-blur-md text-white px-6 py-3 font-subhead uppercase tracking-widest border border-white/30 hover:bg-white/20 transition-colors text-sm">
                Free Estimate
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-6 text-sm text-slate-100/90">
              <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-[color:var(--ww-gold)]" /> Fully insured</div>
              <div className="flex items-center gap-2"><Star className="h-5 w-5 text-[color:var(--ww-gold)]" /> Locally owned &amp; operated</div>
              <div className="flex items-center gap-2"><Clock className="h-5 w-5 text-[color:var(--ww-gold)]" /> Weather-aware scheduling</div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section className="py-20 md:py-28 px-6 md:px-12 max-w-7xl mx-auto ww-water-bg">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <div className="text-xs font-subhead tracking-widest text-[color:var(--ww-red)] uppercase mb-3">What we clean</div>
            <h2 className="font-display uppercase text-4xl sm:text-5xl text-[color:var(--ww-navy)]">Pro pressure washing.<br /> serious results.</h2>
          </div>
          <Link to="/services" className="ww-btn-outline inline-flex items-center gap-2 text-sm">
            All services <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid={T.home.servicesGrid}>
          {services.map((s) => {
            const Icon = iconMap[s.icon] || Droplets;
            return (
              <Link
                key={s.id}
                to={`/services#${s.slug}`}
                className="ww-card ww-shadow-hover p-7 flex flex-col gap-4 group"
              >
                <span className="h-12 w-12 rounded-md bg-[color:var(--ww-navy)] text-[color:var(--ww-gold)] flex items-center justify-center">
                  <Icon className="h-6 w-6" />
                </span>
                <h3 className="font-display uppercase text-2xl text-[color:var(--ww-navy)] leading-tight">{s.name}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.description}</p>
                <div className="mt-auto pt-2 flex items-center justify-between text-sm">
                  <span className="text-slate-500 font-subhead">From <strong className="text-[color:var(--ww-navy)]">${s.base_price?.toFixed?.(0)}</strong></span>
                  <span className="text-[color:var(--ww-red)] font-subhead uppercase tracking-widest text-xs inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    Learn more <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* TRUST SECTION — Commercial-Grade Equipment. Residential-Grade Care. */}
      <section className="py-20 md:py-28 bg-[color:var(--ww-navy)] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="text-xs font-subhead tracking-widest text-[color:var(--ww-gold)] uppercase mb-3">Why Wonder Washers</div>
            <h2 className="font-display uppercase text-4xl sm:text-5xl leading-tight">
              Commercial-Grade Equipment.<br />
              <span className="text-[color:var(--ww-gold)]">Residential-Grade Care.</span>
            </h2>
            <p className="mt-5 text-slate-300 leading-relaxed max-w-2xl">
              The same industrial rig trusted for commercial routes is fully adjustable — dial it down for painted siding and cedar decks, dial it up to strip years of Oregon grime off concrete. One rig, every job done right.
            </p>
            <p className="mt-3 text-slate-300 leading-relaxed max-w-2xl">
              Our system can deliver 3,000 PSI. The expertise is knowing when a cedar deck should never see it.
            </p>
            <div className="grid sm:grid-cols-2 gap-6 mt-10">
              {[
                { icon: Flame, t: "Hot water + steam", d: "Up to 248°F cuts through grease, gum, oil, and organic growth on driveways and commercial flatwork — cold water can't." },
                { icon: Droplets, t: "Soft-wash for siding & roofs", d: "Low-pressure chemical application safely lifts algae and mildew from siding and roofs — won't strip paint, no shingle granule loss." },
                { icon: Sparkles, t: "Wood-safe on cedar decks", d: "Fully adjustable pressure and wide-fan nozzles, cleaned with the grain — no fuzzing, gouging, or wand marks." },
                { icon: ShieldCheck, t: "Our water, not yours", d: "330-gallon onboard tank means jobs don't depend on your spigot, well, or water pressure — and no water intrusion behind your walls." },
              ].map((b) => (
                <div key={b.t} className="border-l-2 border-[color:var(--ww-red)] pl-5">
                  <div className="flex items-center gap-2">
                    <b.icon className="h-4 w-4 text-[color:var(--ww-gold)]" />
                    <div className="font-subhead uppercase tracking-widest text-base">{b.t}</div>
                  </div>
                  <div className="text-sm text-slate-300 mt-2 leading-relaxed">{b.d}</div>
                </div>
              ))}
            </div>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/book" className="ww-btn-primary inline-flex items-center gap-2">Book my service <ArrowRight className="h-4 w-4" /></Link>
              <a href="tel:[PHONE]" className="inline-flex items-center gap-2 text-slate-200 font-subhead uppercase tracking-widest hover:text-white text-sm">
                <PhoneCall className="h-4 w-4 text-[color:var(--ww-gold)]" /> [PHONE]
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative rounded-md overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1721815693498-cc28507c0ba2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBob3VzZSUyMGV4dGVyaW9yfGVufDB8fHx8MTc4NTk1NjQzM3ww&ixlib=rb-4.1.0&q=85"
                alt="Oregon home exterior after Wonder Washers house washing"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute bottom-5 left-5 right-5 rounded-md bg-white/10 backdrop-blur-md p-4 border border-white/20">
                <div className="flex items-center gap-1 mb-1">
                  {[0,1,2,3,4].map((i) => <Star key={i} className="h-4 w-4 text-[color:var(--ww-gold)] fill-[color:var(--ww-gold)]" />)}
                </div>
                <div className="text-sm text-white">"Gentle on our siding, tough on the grime. Booking took two minutes."</div>
                <div className="text-xs text-slate-300 mt-1">— Sarah K., Eugene</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 px-6 md:px-12 max-w-7xl mx-auto">
        <div className="ww-card-lg p-10 md:p-14 flex flex-col md:flex-row md:items-center justify-between gap-8 ww-water-bg">
          <div className="max-w-xl">
            <h3 className="font-display uppercase text-4xl text-[color:var(--ww-navy)] leading-tight">Ready for a property people notice?</h3>
            <p className="text-slate-600 mt-3">Pick a time, we handle the rest — confirmations, weather-aware reminders, and sparkling clean results.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/book" className="ww-btn-primary">Book Online</Link>
            <Link to="/quote" className="ww-btn-outline">Free Estimate</Link>
          </div>
        </div>
      </section>
    </>
  );
}
